'use client';
import { authLogger } from '@/core/utils/logger';

import { useState, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useLoginMutation } from '@/api/features/auth';
import { extractErrorMessage } from '@/shared/utils/toast';
import { config } from '@/config';
import { useCsrfToken } from '@/core/hooks/useCsrfToken';
import { useAppInitialization } from '@/core/providers/AppInitializationProvider';
import { setGlobalCsrfToken } from '@/core/providers/CsrfProvider';

function PlatformLoginContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifyingAccess, setVerifyingAccess] = useState(false);
  const searchParams = useSearchParams();
  const { secureFetch } = useCsrfToken();
  const { isReady: appReady, waitUntilReady } = useAppInitialization();

  const [loginMutation] = useLoginMutation({
    fixedCacheKey: 'platform-login-mutation'
  });

  const returnUrl = searchParams.get('returnUrl') || '/platform/organizations';

  // NOTE: CSRF is handled globally by CsrfProvider and is not required for auth/login endpoints.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password');
      return;
    }

    setIsSubmitting(true);

    try {
      // Wait for app initialization to prevent "first request fails" issues
      if (!appReady) {
        authLogger.debug('[PlatformLogin] Waiting for app initialization...');
        try {
          await Promise.race([
            waitUntilReady(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
          ]);
          authLogger.debug('[PlatformLogin] App initialization complete');
        } catch {
          authLogger.warn('[PlatformLogin] App initialization timeout, proceeding anyway');
        }
      }

      // Small delay to ensure middleware is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 1: Login with credentials
      const result = await loginMutation({ email: email.trim(), password, clientType: 'web' }).unwrap();

      if (!result.user || !result.session?.sessionToken) {
        setError('Login failed. Please check your credentials.');
        setIsSubmitting(false);
        return;
      }

      // Wait for cookies to be processed (increased from 500ms to handle slow networks)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Poll for CSRF token similar to auth login page
      const getCsrfFromCookie = (): string | null => {
        const match = document.cookie.match(/(?:^|; )csrf-token=([^;]+)/);
        if (match) {
          try {
            return decodeURIComponent(match[1]);
          } catch {
            return match[1];
          }
        }
        return null;
      };

      // Poll until cookie appears or timeout (max 3 seconds additional)
      const csrfPollStart = Date.now();
      let csrfToken: string | null = null;
      while (!(csrfToken = getCsrfFromCookie()) && (Date.now() - csrfPollStart) < 3000) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      if (csrfToken) {
        setGlobalCsrfToken(csrfToken);
      }

      // Step 2: Verify platform admin access
      setVerifyingAccess(true);
      
      try {
        const verifyRes = await secureFetch(
          `${config.api.baseUrl}/platform/organizations`,
          { method: 'GET' }
        );

        if (verifyRes.status === 403) {
          setError('Access denied. You do not have platform administrator privileges.');
          setIsSubmitting(false);
          setVerifyingAccess(false);
          return;
        }

        if (verifyRes.status === 401) {
          setError('Session expired. Please try logging in again.');
          setIsSubmitting(false);
          setVerifyingAccess(false);
          return;
        }

        if (!verifyRes.ok) {
          throw new Error('Failed to verify platform access');
        }

        // Cache auth state so SecureAuthProvider sees the user on the next page load.
        // PlatformProtectedRoute checks isAuthenticated; without this cache, auth state
        // is not updated by this flow and the user gets redirected back to login.
        sessionStorage.setItem('auth_state_cache', JSON.stringify({
          user: result.user,
          timestamp: Date.now(),
        }));

        // Full-page redirect so the destination loads with cookies and cached auth state.
        // (Same pattern as main login page — router.replace leaves auth context stale.)
        let target = '/platform/organizations';
        try {
          const decoded = decodeURIComponent(returnUrl);
          if (decoded && decoded.startsWith('/')) {
            target = decoded;
          }
        } catch {
          // ignore malformed returnUrl
        }
        window.location.href = target;
      } catch (verifyErr: unknown) {
        authLogger.error('[PlatformLogin] Verification error:', verifyErr);
        setError('Failed to verify platform access. Please try again.');
        setIsSubmitting(false);
        setVerifyingAccess(false);
      }
    } catch (err: unknown) {
      authLogger.error('[PlatformLogin] Login error:', err);

      // .unwrap() throws RtkQueryError directly
      setError(extractErrorMessage(err, 'Login failed'));

      setIsSubmitting(false);
      setVerifyingAccess(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Platform Admin</h1>
            <p className="text-sm text-slate-500 mt-1">
              Sign in with your administrator account
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !email.trim() || !password.trim()}
              className="w-full py-2.5 px-4 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {verifyingAccess ? 'Verifying access...' : 'Signing in...'}
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs text-center text-slate-500">
              This is a restricted area for authorized platform administrators only.
              <br />
              Unauthorized access attempts are logged and monitored.
            </p>
          </div>
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <Link 
            href="/home" 
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Voley
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function PlatformLoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    }>
      <PlatformLoginContent />
    </Suspense>
  );
}
